import React, {useEffect, useState} from "react"
import { Chart, LinearScale, ArcElement, CategoryScale, Title, PointElement, LineElement } from 'chart.js';
import { Pie, Line } from 'react-chartjs-2';
import { useInView } from 'react-intersection-observer';
import ChartDataLabels from 'chartjs-plugin-datalabels';


Chart.register(PointElement, ArcElement, LineElement, CategoryScale, LinearScale, Title);
Chart.register(ChartDataLabels);

const isBrowser = typeof window !== 'undefined';

const Graphs = ({ data, index }) => {

    const { ref, inView, entry } = useInView({
        threshold: .5,
        delay: 100,
        triggerOnce: true
    });

    if (data) {
        const { chartType, lineChart, pieChart } = data
        {switch (chartType) {
            case 'pie':
                return (
                    <div ref={ref}>
                        <PieGraph inView={inView} index={index} data={pieChart} />
                    </div>
                )
            case 'line':
                return (
                    <div ref={ref}>
                        <LineGraph inView={inView} index={index} data={lineChart} title="This is the Title" subtitle="Subtitle here" />
                    </div>
                )
    
        }}
    }
    return (
        <p>Error loading charts</p>
    ) 
}

const PieGraph = ({ data, index, inView }) => {

    const [chartData, setChartData] = useState(null)

    let pos = 'right';
    let lw = 30
    let lh = 30
    let fs = 22
    let pad = 40

    if (isBrowser) {
        if (window.innerWidth < 700) {
            pos = 'bottom';
            lw = 10
            lh = 10
            fs = 12
            pad = 5
        }
    }   

    const chartOptions = {
        scales: {
            y: {
                display: false
            }
        },
        plugins: {
            legend: {
                position: pos,
                reverse: false,
                align: 'center',
                labels: {
                    boxWidth: lw,
                    boxHeight: lh,
                    padding: pad,
                    font: {
                        size: fs
                    }
                }
            },
            datalabels: {
                display: 'auto',
                color: '#1B5375',
                font: {
                    size: '20px',
                    weight: 'bold'
                },
                borderWidth: 1,
                backgroundColor: '#D8D8D8',
                borderRadius: 10,
                anchor: 'center',
                offset: 10,
                formatter: (value) => {
                    return value + '%';
                }
            },
        },
        animation: {
            duration: 2500
        },
    }

    useEffect (() => {
        if (data) {
            const chartData = data.data
            if (chartData) {
                const labels = chartData.map(item => item?.label)
                const values = chartData.map(item => item?.value)
                const backgroundColors = chartData.map(item => item?.color)

                if (inView && labels) {
                    setChartData({
                        labels: labels,
                        datasets: [{
                            label: 'none',
                            data: values,
                            backgroundColor: backgroundColors,
                            borderWidth: 0,
                        }]
                    })
                }
                
            }
        }
    }, [data, inView])

    if (chartData) {
        return (
            <section className="chart pie">
                <h6 className="pie-title">{data.title}</h6>
                <div className="wrapper pie">
                    <Pie id={`pie-${index}`} data={chartData} options={chartOptions} />
                </div>
            </section>
        )
    } else {
        return (
            <p>Error loading charts</p>
        )
    }
}

const LineGraph = ({ data, index, inView }) => {

    const [selectedGroup, setSelectedGroup] = useState(0)
    
    const [chartData, setChartData] = useState(null)
    const [chartGroups, setChartGroups] = useState(null)
    const [suggestedMin, setSuggestedMin] = useState(0)

    let pos = 'right';
    let lw = 30
    let lh = 30
    let fs = 22

    if (isBrowser) {
        if (window.innerWidth < 700) {
            pos = 'bottom';
            lw = 10
            lh = 10
            fs = 12
        }
    }

    const chartOptions = {
        scales: {
            x: {
                display: true,
            },
            y: {
                suggestedMin: suggestedMin,
            }
        },
        responsive: true,
        plugins: {
            legend: {
                position: pos,
                align: 'start',
                labels: {
                    boxWidth: lw,
                    boxHeight: lh,
                    font: {
                        size: fs,
                        weight: '700'
                    }
                }
            },
            datalabels: {
                display: false
            }
        },
        aspectRatio: 2,
    }

    useEffect(() => {
        if (data) {
            setChartGroups(data.group.map(item => item.title))
        }
    }, [data])


    useEffect(() => {
        if (data) {
            const selectedData = data.group[selectedGroup].data

            if (setChartData) {
                const labels = selectedData.map(item => item?.label)
                const values = selectedData.map(item => item.value === null ? item.value = 0 : item?.value)
                let minValue = Math.min(...values)

                if (minValue >= 2) {
                    setSuggestedMin(minValue - 2)
                } else if (minValue = 1) {
                    setSuggestedMin(minValue - 1)
                } else {
                    setSuggestedMin(minValue)
                }

                if (inView && labels) {
                    setChartData({
                        labels: labels,
                        datasets: [
                            {
                                label: data.group[selectedGroup].title,
                                data: values,
                                borderColor: 'rgba(27, 83, 117, 0.7)',
                                backgroundColor: 'rgba(27, 83, 117, 0.2)',
                                fill: true
                            },
                            {
                                label: 'Clinical Cutoff',
                                data: new Array(values.length).fill(data.group[selectedGroup].clinicalCutoff),
                                borderColor: 'rgba(216, 81, 35, 0.7)',
                                backgroundColor: 'rgba(216, 81, 35, 0.2)',
                                fill: true
                            }
                        ]
                    })
                }
            }
        }
    }, [selectedGroup, inView])

    const handleGroupChange = (e) => {
        const index = e.target.value
        setSelectedGroup(index)
    }

    if (chartData) {
        return (
            <section className="chart line">
                <div className="chart-info">
                    <h6>{data.title}</h6>
                    <h3>{data.subtitle}</h3>

                    {selectedGroup ? (
                        <p>{data.group[selectedGroup].descriptor}</p>
                    ) : null}
                </div>
                <label>Select subscore to explore the data</label>
                <select className="chart-select" onChange={(e) => handleGroupChange(e)}>
                    {chartGroups ? chartGroups.map((item, i) => {
                        return (
                            <option key={i} value={i}>{item}</option>
                        )
                    }) : ''}
                </select>
                <div className="wrapper">
                    <Line id={`line-${index}`} data={chartData} options={chartOptions} />
                </div>
            </section>
        )
    } else {
        return (
            <p>Error loading charts</p>
        )
    }
}

export default Graphs
